/**
 * @tanstack/react-query hooks for the Hiring (ATS) module (Phase H.8).
 * Thin wrappers over lib/api/hiring.ts so pages stay declarative and
 * caching/invalidation lives in one place.
 *
 * Query-key convention mirrors use-hr.ts / use-posh.ts:
 *   ["hiring", <area>, ...params]   — flush the whole module with
 *                                     hiringKeys.all.
 *
 * Invalidation rules:
 *  - A stage move / withdraw / interview schedule changes the
 *    requisition pipeline AND can change the requisition list framing,
 *    so flush both (the cheapest correct rule — same posture as the
 *    leave/asset list-flush).
 *  - The Hire handoff mints a brand-new loginable employee → flush the
 *    shared people directory too (directoryKeys.all), exactly like
 *    useCreateEmployee, so the new joiner shows up on /dashboard/people
 *    + every picker immediately. It also moves the application to
 *    `hired` and assigns onboarding → flush the pipeline + the HR
 *    onboarding lists.
 *  - The visibility-scoped feedback list 404s a caller with no read
 *    relationship (that is "not visible", NOT an error) — the query
 *    must NOT retry a hard authz fail (retry:false), and the caller
 *    treats a not_found as an empty list.
 *
 * The shared QueryClient (components/providers.tsx) sets retry: 1,
 * refetchOnWindowFocus: false, staleTime: 30s. Stage list is a slow-
 * moving catalogue → longer staleTime.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { hiringApi } from "@/lib/api";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import { hrKeys } from "@/hooks/use-hr";
import { directoryKeys } from "@/hooks/use-directory";
import type {
  ApprovalDecisionInput,
  HiringAnalyticsFilters,
  CreateOfferInput,
  SalaryCalcRequest,
  CreateRequisitionInput,
  HireApplicantInput,
  HiringCostInput,
  HiringCostListFilters,
  HiringCostReportFilters,
  HiringReportFilters,
  JDTemplateInput,
  RequisitionQuestionInput,
  MoveApplicationInput,
  OfferDecisionInput,
  PatchInterviewInput,
  ScheduleInterviewInput,
  SendCandidateMessageInput,
  ScorecardTemplateInput,
  SubmitFeedbackInput,
  SubmitReferralInput,
  UpdateCareersBrandingInput,
  UpdateEmailTemplateInput,
  UpdateHiringEmailSettingsInput,
  UpdateHiringApprovalSettingsInput,
  UpdateOfferInput,
  UpdateRequisitionInput,
  CreateSlotOfferInput,
  PatchCandidateInput,
  InterviewScheduleFilters,
} from "@/types/hiring";

const CATALOGUE_STALE = 5 * 60 * 1000; // stages change rarely

export const hiringKeys = {
  all: ["hiring"] as const,
  requisitions: (status?: string) =>
    [...hiringKeys.all, "requisitions", status ?? "all"] as const,
  pipeline: (id: number) =>
    [...hiringKeys.all, "pipeline", id] as const,
  stages: () => [...hiringKeys.all, "stages"] as const,
  candidates: (source?: string) =>
    [...hiringKeys.all, "candidates", source ?? "all"] as const,
  candidate: (id: number) =>
    [...hiringKeys.all, "candidate", id] as const,
  myInterviews: () => [...hiringKeys.all, "my-interviews"] as const,
  interviewSchedule: (f: InterviewScheduleFilters) =>
    [
      ...hiringKeys.all,
      "interview-schedule",
      f.when,
      f.requisition_id ?? 0,
      f.interviewer_id ?? 0,
      f.page ?? 1,
      f.per_page ?? 0,
    ] as const,
  jdTemplates: () => [...hiringKeys.all, "jd-templates"] as const,
  scorecardTemplates: () =>
    [...hiringKeys.all, "scorecard-templates"] as const,
  interviewFeedback: (interviewId: number) =>
    [...hiringKeys.all, "interview-feedback", interviewId] as const,
  offerForApplication: (applicationId: number) =>
    [...hiringKeys.all, "offer-for-app", applicationId] as const,
  reportsSummary: (filters?: HiringReportFilters) =>
    [
      ...hiringKeys.all,
      "reports-summary",
      filters?.from ?? "",
      filters?.to ?? "",
      filters?.requisition_id ?? 0,
      filters?.hiring_manager_id ?? 0,
      filters?.department ?? "",
    ] as const,
  requisitionReport: (id: number) =>
    [...hiringKeys.all, "requisition-report", id] as const,
  candidateApplications: (candidateId: number) =>
    [...hiringKeys.all, "candidate-applications", candidateId] as const,
  applicationInterviews: (applicationId: number) =>
    [...hiringKeys.all, "application-interviews", applicationId] as const,
  // K2.4 screening questions (per requisition) + an application's answers.
  requisitionQuestions: (requisitionId: number) =>
    [...hiringKeys.all, "requisition-questions", requisitionId] as const,
  applicationAnswers: (applicationId: number) =>
    [...hiringKeys.all, "application-answers", applicationId] as const,
  // K5.2 cost ledger + cost-per-hire report.
  costs: (filters?: HiringCostListFilters) =>
    [
      ...hiringKeys.all,
      "costs",
      filters?.from ?? "",
      filters?.to ?? "",
      filters?.requisition_id ?? 0,
      filters?.category ?? "",
    ] as const,
  costReport: (filters?: HiringCostReportFilters) =>
    [
      ...hiringKeys.all,
      "cost-report",
      filters?.from ?? "",
      filters?.to ?? "",
      filters?.requisition_id ?? 0,
    ] as const,
  // K5.1 deep analytics aggregate.
  analytics: (filters?: HiringAnalyticsFilters) =>
    [
      ...hiringKeys.all,
      "analytics",
      filters?.from ?? "",
      filters?.to ?? "",
      filters?.requisition_id ?? 0,
      filters?.hiring_manager_id ?? 0,
      filters?.department ?? "",
    ] as const,
  // K2.2 slot offers per application.
  slotOffers: (applicationId: number) =>
    [...hiringKeys.all, "slot-offers", applicationId] as const,
};

// A deterministic 4xx (the page hard role-gates, but if the API still
// 4xxs the report can never succeed on retry) — short-circuit the
// retry so the page renders the "no access" / error state at once.
// Mirrors retryUnlessClientError from use-posh.ts (the compliance
// dashboard pattern this report follows).
function retryUnlessClientError(failureCount: number, err: unknown): boolean {
  const status = (err as AxiosError | undefined)?.response?.status;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 1;
}

// ---------------------------------------------------------------------------
// Requisitions
// ---------------------------------------------------------------------------

export function useRequisitions(status?: string, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.requisitions(status),
    queryFn: ({ signal }) =>
      hiringApi.listRequisitions(
        status ? { status } : undefined,
        signal,
      ),
    enabled,
  });
}

/** Flush every requisition-list bucket (status filters) at once. */
function invalidateRequisitions(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({
    queryKey: [...hiringKeys.all, "requisitions"],
  });
}

export function useCreateRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRequisitionInput) =>
      hiringApi.createRequisition(body),
    onSuccess: () => invalidateRequisitions(qc),
  });
}

export function useUpdateRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: UpdateRequisitionInput;
    }) => hiringApi.patchRequisition(vars.id, vars.body),
    onSuccess: (_data, vars) => {
      invalidateRequisitions(qc);
      void qc.invalidateQueries({
        queryKey: hiringKeys.pipeline(vars.id),
      });
    },
  });
}

export function useRequisitionPipeline(id: number, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.pipeline(id),
    queryFn: ({ signal }) =>
      hiringApi.getRequisitionPipeline(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

// ---------------------------------------------------------------------------
// Pipeline stages
// ---------------------------------------------------------------------------

export function useStages(enabled = true) {
  return useQuery({
    queryKey: hiringKeys.stages(),
    queryFn: ({ signal }) => hiringApi.listStages(signal),
    staleTime: CATALOGUE_STALE,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// JD templates + scorecard templates (Wave K1.6)
// Slow-moving catalogues (the stages staleTime rule). Mutations are
// HR-admin server-side; the settings pages also UX-gate.
// ---------------------------------------------------------------------------

export function useJDTemplates(enabled = true) {
  return useQuery({
    queryKey: hiringKeys.jdTemplates(),
    queryFn: ({ signal }) => hiringApi.listJDTemplates(signal),
    staleTime: CATALOGUE_STALE,
    enabled,
  });
}

function invalidateJDTemplates(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: hiringKeys.jdTemplates() });
}

export function useCreateJDTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: JDTemplateInput) =>
      hiringApi.createJDTemplate(body),
    onSuccess: () => invalidateJDTemplates(qc),
  });
}

export function useUpdateJDTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: JDTemplateInput }) =>
      hiringApi.updateJDTemplate(vars.id, vars.body),
    onSuccess: () => invalidateJDTemplates(qc),
  });
}

export function useDeleteJDTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hiringApi.deleteJDTemplate(id),
    onSuccess: () => invalidateJDTemplates(qc),
  });
}

// ---------------------------------------------------------------------------
// K2.4 — screening / knockout questions (HR authoring, per requisition).
// ---------------------------------------------------------------------------

export function useRequisitionQuestions(requisitionId: number, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.requisitionQuestions(requisitionId),
    queryFn: ({ signal }) =>
      hiringApi.listRequisitionQuestions(requisitionId, signal),
    enabled: enabled && Number.isFinite(requisitionId) && requisitionId > 0,
  });
}

function invalidateRequisitionQuestions(
  qc: ReturnType<typeof useQueryClient>,
  requisitionId: number,
) {
  void qc.invalidateQueries({
    queryKey: hiringKeys.requisitionQuestions(requisitionId),
  });
}

export function useCreateRequisitionQuestion(requisitionId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RequisitionQuestionInput) =>
      hiringApi.createRequisitionQuestion(requisitionId, body),
    onSuccess: () => invalidateRequisitionQuestions(qc, requisitionId),
  });
}

export function useUpdateRequisitionQuestion(requisitionId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: RequisitionQuestionInput }) =>
      hiringApi.updateRequisitionQuestion(vars.id, vars.body),
    onSuccess: () => invalidateRequisitionQuestions(qc, requisitionId),
  });
}

export function useDeleteRequisitionQuestion(requisitionId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hiringApi.deleteRequisitionQuestion(id),
    onSuccess: () => invalidateRequisitionQuestions(qc, requisitionId),
  });
}

// GET /hiring/applications/{id}/answers — the HR knockout-review surface.
export function useApplicationAnswers(applicationId: number, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.applicationAnswers(applicationId),
    queryFn: ({ signal }) =>
      hiringApi.listApplicationAnswers(applicationId, signal),
    enabled: enabled && Number.isFinite(applicationId) && applicationId > 0,
  });
}

export function useScorecardTemplates(enabled = true) {
  return useQuery({
    queryKey: hiringKeys.scorecardTemplates(),
    queryFn: ({ signal }) =>
      hiringApi.listScorecardTemplates(signal),
    staleTime: CATALOGUE_STALE,
    enabled,
  });
}

function invalidateScorecardTemplates(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({
    queryKey: hiringKeys.scorecardTemplates(),
  });
}

export function useCreateScorecardTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ScorecardTemplateInput) =>
      hiringApi.createScorecardTemplate(body),
    onSuccess: () => invalidateScorecardTemplates(qc),
  });
}

export function useUpdateScorecardTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: ScorecardTemplateInput;
    }) => hiringApi.updateScorecardTemplate(vars.id, vars.body),
    onSuccess: () => {
      invalidateScorecardTemplates(qc);
      // Open feedback dialogs resolve labels from the catalogue; an
      // edited template can change what they render.
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "interview-feedback"],
      });
    },
  });
}

export function useDeleteScorecardTemplate() {
  const qc = useQueryClient();
  return useMutation({
    // A 409 `template_in_use` rejects — the caller surfaces the message
    // (the template stays; rename it instead).
    mutationFn: (id: number) =>
      hiringApi.deleteScorecardTemplate(id),
    onSuccess: () => invalidateScorecardTemplates(qc),
  });
}

// ---------------------------------------------------------------------------
// Applications / pipeline
// ---------------------------------------------------------------------------

/**
 * A stage move / withdraw changes the pipeline AND the requisition list
 * framing; the candidate profile also shows the application status, so
 * flush the whole module sub-tree rather than guess which ids moved
 * (the leave/asset list-flush rule).
 */
function invalidatePipelineWide(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({ queryKey: hiringKeys.all });
}

export function useMoveApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: MoveApplicationInput;
    }) => hiringApi.moveApplication(vars.id, vars.body),
    onSuccess: () => invalidatePipelineWide(qc),
  });
}

export function useWithdrawApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      hiringApi.withdrawApplication(id),
    onSuccess: () => invalidatePipelineWide(qc),
  });
}

// ---------------------------------------------------------------------------
// Candidates (HR-admin; PII)
// ---------------------------------------------------------------------------

export function useCandidates(source?: string, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.candidates(source),
    queryFn: ({ signal }) =>
      hiringApi.listCandidates(
        source ? { source } : undefined,
        signal,
      ),
    enabled,
  });
}

/**
 * A single candidate. A forbidden/unknown id is a neutral 404 (no
 * existence oracle) — do NOT retry a hard authz fail; the caller maps
 * not_found to a "not available" state.
 */
export function useCandidate(id: number, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.candidate(id),
    queryFn: ({ signal }) => hiringApi.getCandidate(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
    retry: false,
  });
}

function invalidateCandidates(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({
    queryKey: [...hiringKeys.all, "candidates"],
  });
}

export function useCreateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<
      typeof hiringApi.createCandidate
    >[0]) => hiringApi.createCandidate(input),
    onSuccess: () => invalidateCandidates(qc),
  });
}

/**
 * The §4a sourced import. The page drives a sequential per-row UX, so
 * this mutation is a single submit of the whole multipart batch (the
 * backend returns the per-row results contract). On success a new set
 * of candidates + (when a requisition was chosen) sourced applications
 * exist → flush candidates AND the pipeline sub-tree.
 */
export function useImportCandidates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<
      typeof hiringApi.importCandidates
    >[0]) => hiringApi.importCandidates(input),
    onSuccess: () => {
      invalidateCandidates(qc);
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "pipeline"],
      });
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "requisitions"],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Interviews
// ---------------------------------------------------------------------------

export function useMyInterviews(enabled = true) {
  return useQuery({
    queryKey: hiringKeys.myInterviews(),
    queryFn: ({ signal }) => hiringApi.listMyInterviews(signal),
    enabled,
  });
}

/** Manage → Hiring → Interviews: the company-wide schedule (paged). */
export function useInterviewSchedule(filters: InterviewScheduleFilters, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.interviewSchedule(filters),
    queryFn: ({ signal }) => hiringApi.listInterviewSchedule(filters, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useScheduleInterview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      applicationId: number;
      body: ScheduleInterviewInput;
    }) =>
      hiringApi.scheduleInterview(vars.applicationId, vars.body),
    onSuccess: () => {
      // A new interview affects the pipeline cards + the assigned
      // interviewer's "my interviews" + the company schedule.
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "pipeline"],
      });
      void qc.invalidateQueries({
        queryKey: hiringKeys.myInterviews(),
      });
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "interview-schedule"],
      });
      // The candidate page's "Interviews & feedback" panel.
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "application-interviews"],
      });
    },
  });
}

export function usePatchInterview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: PatchInterviewInput;
    }) => hiringApi.patchInterview(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: hiringKeys.myInterviews(),
      });
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "interview-schedule"],
      });
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "pipeline"],
      });
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "application-interviews"],
      });
    },
  });
}

/**
 * Visibility-scoped feedback list. A caller with no read relationship
 * gets a NEUTRAL 404 — that is "no feedback visible", NOT an error.
 * Don't retry a hard authz fail; the caller treats not_found as [].
 */
export function useInterviewFeedback(
  interviewId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: hiringKeys.interviewFeedback(interviewId),
    queryFn: ({ signal }) =>
      hiringApi.listInterviewFeedback(interviewId, signal),
    enabled:
      enabled && Number.isFinite(interviewId) && interviewId > 0,
    retry: false,
  });
}

export function useSubmitInterviewFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      interviewId: number;
      body: SubmitFeedbackInput;
    }) =>
      hiringApi.submitInterviewFeedback(
        vars.interviewId,
        vars.body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: hiringKeys.interviewFeedback(vars.interviewId),
      });
      void qc.invalidateQueries({
        queryKey: hiringKeys.myInterviews(),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Offers
// ---------------------------------------------------------------------------

/**
 * The (at most one) offer for an application. K1.7 / D-4: an authorized
 * caller with no offer gets a 204 → `data === null` ("no offer yet" —
 * the caller renders the create-offer path; devtools stays clean). A
 * forbidden caller still gets the NEUTRAL 404 (no oracle) — don't
 * retry that.
 */
/**
 * The offer salary calculator (HR-admin): every line of the salary for the
 * given start (yearly CTC / monthly gross / monthly take-home). Keeps the
 * last answer on screen while the next one loads; off while there is no
 * amount.
 */
export function useOfferSalaryCalc(params: SalaryCalcRequest | null) {
  return useQuery({
    queryKey: [...hiringKeys.all, "salary-calc", params] as const,
    queryFn: ({ signal }) => hiringApi.postSalaryCalc(params!, signal),
    enabled: params != null && params.amount_paise > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useOfferForApplication(
  applicationId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: hiringKeys.offerForApplication(applicationId),
    queryFn: ({ signal }) =>
      hiringApi.getOfferForApplication(applicationId, signal),
    enabled:
      enabled &&
      Number.isFinite(applicationId) &&
      applicationId > 0,
    retry: false,
  });
}

function invalidateOffer(
  qc: ReturnType<typeof useQueryClient>,
  applicationId: number,
) {
  void qc.invalidateQueries({
    queryKey: hiringKeys.offerForApplication(applicationId),
  });
  // An offer decision can change the funnel framing on the pipeline —
  // and sending / accepting moves the application to the Offer stage.
  void qc.invalidateQueries({
    queryKey: [...hiringKeys.all, "pipeline"],
  });
  void qc.invalidateQueries({
    queryKey: [...hiringKeys.all, "candidate-applications"],
  });
}

export function useCreateOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      applicationId: number;
      body: CreateOfferInput;
    }) => hiringApi.createOffer(vars.applicationId, vars.body),
    onSuccess: (_data, vars) =>
      invalidateOffer(qc, vars.applicationId),
  });
}

/**
 * K1.1 — PATCH a DRAFT offer (ctc / joining date / component breakup).
 * A sum mismatch is a server 422 `ctc_component_mismatch` (the editor
 * mirrors the check and disables save, so hitting it means a race);
 * a non-draft offer is 409 `not_editable`.
 */
export function useUpdateOffer(applicationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      offerId: number;
      body: UpdateOfferInput;
    }) => hiringApi.updateOffer(vars.offerId, vars.body),
    onSuccess: () => invalidateOffer(qc, applicationId),
  });
}

export function useSendOffer(applicationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (offerId: number) =>
      hiringApi.sendOffer(offerId),
    onSuccess: () => invalidateOffer(qc, applicationId),
  });
}

export function useDecideOffer(applicationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      offerId: number;
      body: OfferDecisionInput;
    }) => hiringApi.decideOffer(vars.offerId, vars.body),
    onSuccess: () => invalidateOffer(qc, applicationId),
  });
}

// ---------------------------------------------------------------------------
// Hire → Onboard handoff
// ---------------------------------------------------------------------------

/**
 * The differentiator. On success a brand-new loginable employee exists
 * AND the application moved to `hired` AND onboarding was assigned, so
 * flush:
 *  - the shared people directory (directoryKeys.all) — exactly like
 *    useCreateEmployee, so the new joiner appears on /dashboard/people
 *    + every picker immediately;
 *  - the HR onboarding lists (hrKeys "onboarding" sub-tree) — the new
 *    instance appears there;
 *  - the whole hiring module (pipeline + requisitions + candidate) —
 *    the application is now hired.
 * The mutation does NOT swallow errors — the caller maps the backend's
 * { error, field } envelope (email_taken / no_consent / not_hireable /
 * invalid_role / …) and surfaces the temp password + onboarding_skipped
 * warning from the success body.
 */
export function useHireApplicant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      applicationId: number;
      body: HireApplicantInput;
    }) => hiringApi.hireApplicant(vars.applicationId, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: directoryKeys.all });
      // 2026-06-04: hiring creates a `users` row identically to
      // `useCreateEmployee`. Without this flush the HR-admin People
      // page (which reads `hrKeys.employees()`) stayed stale until
      // refetch. Mirror of use-hr.ts useCreateEmployee's dual-flush.
      void qc.invalidateQueries({ queryKey: hrKeys.employees() });
      void qc.invalidateQueries({
        queryKey: [...hrKeys.all, "onboarding"],
      });
      void qc.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

/** Wave-B: hire-form defaults (accepted offer + requisition). HR-admin. */
export function useHireDefaults(applicationId: number, enabled = true) {
  return useQuery({
    queryKey: [...hiringKeys.all, "hire-defaults", applicationId] as const,
    queryFn: ({ signal }) => hiringApi.getHireDefaults(applicationId, signal),
    enabled: enabled && Number.isFinite(applicationId) && applicationId > 0,
    retry: false,
  });
}

/** Wave-B: HR edits a candidate's notes / phone / pay / notice period. */
export function usePatchCandidate(candidateId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchCandidateInput) =>
      hiringApi.patchCandidate(candidateId, body),
    onSuccess: (res) => {
      qc.setQueryData(hiringKeys.candidate(candidateId), res);
      invalidateCandidates(qc);
    },
  });
}

/** Wave-B: HR erases a candidate permanently (DPDP). */
export function useEraseCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: number) => hiringApi.eraseCandidate(candidateId),
    onSuccess: (_res, candidateId) => {
      qc.removeQueries({ queryKey: hiringKeys.candidate(candidateId) });
      void qc.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Reports (H.10a) — GET /api/v1/hiring/reports/summary aggregate. HR-admin
// gated server-side AND by the page's HrAdminGate, so a non-admin never
// fires this query. No candidate PII on the wire — counts + requisition
// id/name/dept that the HR admin already sees on the pipeline surface.
// ---------------------------------------------------------------------------

export function useHiringReportsSummary(
  filters?: HiringReportFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: hiringKeys.reportsSummary(filters),
    queryFn: ({ signal }) =>
      hiringApi.getReportsSummary(filters, signal),
    enabled,
    // The hard role-gate prevents firing for a non-admin; a 403 here
    // would be defence-in-depth only. Don't burn retries on a
    // deterministic 4xx — surface it (the page renders the neutral
    // "couldn't load" alert).
    retry: retryUnlessClientError,
  });
}

/**
 * K1.7 per-requisition drill-down report. HR-admin OR the owning
 * hiring manager server-side; a non-owner gets a neutral 404 — don't
 * retry a deterministic 4xx.
 */
export function useRequisitionReport(id: number, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.requisitionReport(id),
    queryFn: ({ signal }) =>
      hiringApi.getRequisitionReport(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
    retry: retryUnlessClientError,
  });
}

/**
 * K1.7 closeout: the per-candidate application list (the primary
 * source for the candidate profile's application context; the
 * ?application=<id> search param stays a fallback). Visibility mirrors
 * the candidate profile — a forbidden caller's neutral 404 is "not
 * available", not an error; don't retry it.
 */
export function useCandidateApplications(
  candidateId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: hiringKeys.candidateApplications(candidateId),
    queryFn: ({ signal }) =>
      hiringApi.listCandidateApplications(candidateId, signal),
    enabled:
      enabled && Number.isFinite(candidateId) && candidateId > 0,
    retry: false,
  });
}

/**
 * K1.7 closeout: every interview round on an application (HR-admin OR
 * owning hiring manager; neutral 404 otherwise — treat as "not
 * visible", don't retry).
 */
export function useApplicationInterviews(
  applicationId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: hiringKeys.applicationInterviews(applicationId),
    queryFn: ({ signal }) =>
      hiringApi.listApplicationInterviews(applicationId, signal),
    enabled:
      enabled && Number.isFinite(applicationId) && applicationId > 0,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// K1.3 — Candidate email comms (templates / settings / timeline / send)
// ---------------------------------------------------------------------------

const k13Keys = {
  emailTemplates: () =>
    [...hiringKeys.all, "email-templates"] as const,
  emailSettings: () =>
    [...hiringKeys.all, "email-settings"] as const,
  candidateMessages: (candidateId: number) =>
    [...hiringKeys.all, "candidate-messages", candidateId] as const,
};

/** HR-admin only (the page role-gates; the server re-checks). */
export function useEmailTemplates(enabled = true) {
  return useQuery({
    queryKey: k13Keys.emailTemplates(),
    queryFn: ({ signal }) => hiringApi.listEmailTemplates(signal),
    staleTime: CATALOGUE_STALE, // templates are catalog admin — slow-moving
    enabled,
    retry: retryUnlessClientError,
  });
}

export function useUpdateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: UpdateEmailTemplateInput;
    }) => hiringApi.updateEmailTemplate(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: k13Keys.emailTemplates(),
      });
    },
  });
}

export function useHiringEmailSettings(enabled = true) {
  return useQuery({
    queryKey: k13Keys.emailSettings(),
    queryFn: ({ signal }) => hiringApi.getEmailSettings(signal),
    enabled,
    retry: retryUnlessClientError,
  });
}

export function useUpdateHiringEmailSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateHiringEmailSettingsInput) =>
      hiringApi.updateEmailSettings(body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: k13Keys.emailSettings(),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// K2.5 — Careers-page branding (accent colour / blurb / logo)
// ---------------------------------------------------------------------------

const careersBrandingKey = () =>
  [...hiringKeys.all, "careers-branding"] as const;

/** HR-admin only (the page role-gates; the server re-checks). */
export function useCareersBranding(enabled = true) {
  return useQuery({
    queryKey: careersBrandingKey(),
    queryFn: ({ signal }) => hiringApi.getCareersBranding(signal),
    staleTime: CATALOGUE_STALE, // branding is slow-moving config
    enabled,
    retry: retryUnlessClientError,
  });
}

export function useUpdateCareersBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCareersBrandingInput) =>
      hiringApi.updateCareersBranding(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: careersBrandingKey() });
    },
  });
}

export function useUploadCareersLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (logo: File) => hiringApi.uploadCareersLogo(logo),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: careersBrandingKey() });
    },
  });
}

export function useDeleteCareersLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => hiringApi.deleteCareersLogo(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: careersBrandingKey() });
    },
  });
}

/**
 * The candidate communication timeline. A caller with no read
 * relationship (not HR-admin, not hiring-manager-on-app) gets a
 * NEUTRAL 404 — that is "not available", NOT an error; don't retry a
 * hard authz fail, the caller hides the section.
 */
export function useCandidateMessages(
  candidateId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: k13Keys.candidateMessages(candidateId),
    queryFn: ({ signal }) =>
      hiringApi.listCandidateMessages(candidateId, signal),
    enabled:
      enabled && Number.isFinite(candidateId) && candidateId > 0,
    retry: false,
  });
}

/**
 * Manual "Email candidate" send. On success the timeline gains a row
 * (sent / failed / suppressed — suppression is a SUCCESS outcome the
 * dialog explains, not an error). The reject-dialog flow does NOT use
 * this hook — its email rides the move mutation body and the move's
 * module-wide flush covers the timeline.
 */
export function useSendCandidateMessage(candidateId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendCandidateMessageInput) =>
      hiringApi.sendCandidateMessage(candidateId, body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: k13Keys.candidateMessages(candidateId),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Referrals (Wave K1.4)
// ---------------------------------------------------------------------------

export const referralKeys = {
  jobs: () => [...hiringKeys.all, "referral-jobs"] as const,
  mine: () => [...hiringKeys.all, "referrals-mine"] as const,
  inbox: (status?: string) =>
    [...hiringKeys.all, "referrals-inbox", status ?? "all"] as const,
};

// The ONLY hiring queries every employee fires. The server returns the
// minimal projection / coarse status — there is nothing to over-cache.
export function useReferralJobs(enabled = true) {
  return useQuery({
    queryKey: referralKeys.jobs(),
    queryFn: ({ signal }) => hiringApi.listReferralJobs(signal),
    enabled,
    retry: retryUnlessClientError,
  });
}

export function useMyReferrals(enabled = true) {
  return useQuery({
    queryKey: referralKeys.mine(),
    queryFn: ({ signal }) => hiringApi.listMyReferrals(signal),
    enabled,
    retry: retryUnlessClientError,
  });
}

export function useSubmitReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitReferralInput) =>
      hiringApi.submitReferral(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: referralKeys.mine() });
      void qc.invalidateQueries({
        queryKey: [...hiringKeys.all, "referrals-inbox"],
      });
    },
  });
}

// HR inbox.
export function useReferralInbox(status?: string, enabled = true) {
  return useQuery({
    queryKey: referralKeys.inbox(status),
    queryFn: ({ signal }) =>
      hiringApi.listReferrals(
        status ? { status } : undefined,
        signal,
      ),
    enabled,
    retry: retryUnlessClientError,
  });
}

/** Flush every referral bucket + the candidate lists (convert mints /
 * links a candidate; hire/pay flips coarse statuses the employee list
 * shows too). */
function invalidateReferrals(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({
    queryKey: [...hiringKeys.all, "referrals-inbox"],
  });
  void qc.invalidateQueries({ queryKey: referralKeys.mine() });
  void qc.invalidateQueries({
    queryKey: [...hiringKeys.all, "candidates"],
  });
}

export function useConvertReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hiringApi.convertReferral(id),
    onSuccess: () => invalidateReferrals(qc),
  });
}

export function useRejectReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hiringApi.rejectReferral(id),
    onSuccess: () => invalidateReferrals(qc),
  });
}

export function usePayReferralBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hiringApi.payReferralBonus(id),
    onSuccess: () => invalidateReferrals(qc),
  });
}

// ---------------------------------------------------------------------------
// K1.2 — Requisition + offer approval chains
// ---------------------------------------------------------------------------

const approvalKeys = {
  settings: () => [...hiringKeys.all, "approval-settings"] as const,
  // Base key with NO discriminator so `invalidateQueries(pending())`
  // prefix-matches both the role-scoped and the excl-self variants.
  pending: (excludeSelf?: boolean) =>
    excludeSelf
      ? ([...hiringKeys.all, "approvals-pending", "excl-self"] as const)
      : ([...hiringKeys.all, "approvals-pending"] as const),
};

/** Flush the approval queue + the requisition/offer lists a decision
 * touched (the badge + the open/send-gate hints follow). */
function invalidateApprovals(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: approvalKeys.pending() });
  void qc.invalidateQueries({ queryKey: [...hiringKeys.all, "requisitions"] });
  void qc.invalidateQueries({ queryKey: [...hiringKeys.all, "offer-for-app"] });
  // The Inbox / tab badges count pending hiring approvals.
  invalidateApprovalsSummary(qc);
}

export function useHiringApprovalSettings(enabled = true) {
  return useQuery({
    queryKey: approvalKeys.settings(),
    queryFn: ({ signal }) => hiringApi.getApprovalSettings(signal),
    enabled,
    retry: retryUnlessClientError,
  });
}

export function useUpdateHiringApprovalSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateHiringApprovalSettingsInput) =>
      hiringApi.updateApprovalSettings(body),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: approvalKeys.settings() }),
  });
}

/** The approver inbox feed (pending requisitions + offers). Gated
 * server-side on the tenant's configured approver_role. */
export function usePendingApprovals(
  enabled = true,
  opts?: { excludeSelf?: boolean },
) {
  return useQuery({
    queryKey: approvalKeys.pending(opts?.excludeSelf),
    queryFn: ({ signal }) =>
      hiringApi.listPendingApprovals(
        signal,
        opts?.excludeSelf ? { exclude_self: true } : undefined,
      ),
    enabled,
    retry: retryUnlessClientError,
    staleTime: 60 * 1000,
  });
}

export function useSubmitRequisitionApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hiringApi.submitRequisitionApproval(id),
    onSuccess: () => invalidateApprovals(qc),
  });
}

export function useDecideRequisitionApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: ApprovalDecisionInput }) =>
      hiringApi.decideRequisitionApproval(vars.id, vars.body),
    onSuccess: () => invalidateApprovals(qc),
  });
}

export function useSubmitOfferApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hiringApi.submitOfferApproval(id),
    onSuccess: () => invalidateApprovals(qc),
  });
}

export function useDecideOfferApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: ApprovalDecisionInput }) =>
      hiringApi.decideOfferApproval(vars.id, vars.body),
    onSuccess: () => invalidateApprovals(qc),
  });
}

// ---------------------------------------------------------------------------
// K5.2 — hiring cost ledger + cost-per-hire
// ---------------------------------------------------------------------------

function invalidateCosts(qc: ReturnType<typeof useQueryClient>) {
  // A cost write moves both the ledger list AND the report — invalidate
  // the whole cost subtree (cheap; both keys share the "costs"/"cost-
  // report" prefix under hiringKeys.all).
  void qc.invalidateQueries({ queryKey: [...hiringKeys.all, "costs"] });
  void qc.invalidateQueries({ queryKey: [...hiringKeys.all, "cost-report"] });
}

export function useHiringCosts(
  filters?: HiringCostListFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: hiringKeys.costs(filters),
    queryFn: ({ signal }) => hiringApi.listHiringCosts(filters, signal),
    enabled,
  });
}

export function useHiringCostReport(
  filters?: HiringCostReportFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: hiringKeys.costReport(filters),
    queryFn: ({ signal }) => hiringApi.getHiringCostReport(filters, signal),
    enabled,
  });
}

// K5.1 deep hiring analytics. HR-admin server-side AND page-gated; a 4xx
// here would be defence-in-depth only — don't burn retries on a
// deterministic client error, surface the neutral error state at once.
export function useHiringAnalytics(
  filters?: HiringAnalyticsFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: hiringKeys.analytics(filters),
    queryFn: ({ signal }) => hiringApi.getAnalytics(filters, signal),
    enabled,
    retry: retryUnlessClientError,
  });
}

export function useCreateHiringCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HiringCostInput) => hiringApi.createHiringCost(body),
    onSuccess: () => invalidateCosts(qc),
  });
}

export function useUpdateHiringCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: HiringCostInput }) =>
      hiringApi.updateHiringCost(vars.id, vars.body),
    onSuccess: () => invalidateCosts(qc),
  });
}

export function useDeleteHiringCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hiringApi.deleteHiringCost(id),
    onSuccess: () => invalidateCosts(qc),
  });
}

// ---------------------------------------------------------------------------
// K2.2 Interview slot offers
// ---------------------------------------------------------------------------

export function useSlotOffers(applicationId: number, enabled = true) {
  return useQuery({
    queryKey: hiringKeys.slotOffers(applicationId),
    queryFn: ({ signal }) =>
      hiringApi.listSlotOffers(applicationId, signal),
    enabled: enabled && Number.isFinite(applicationId) && applicationId > 0,
  });
}

export function useCreateSlotOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { applicationId: number; body: CreateSlotOfferInput }) =>
      hiringApi.createSlotOffer(vars.applicationId, vars.body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: hiringKeys.slotOffers(vars.applicationId),
      });
    },
  });
}

export function useCancelSlotOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { applicationId: number; offerId: number }) =>
      hiringApi.cancelSlotOffer(vars.applicationId, vars.offerId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: hiringKeys.slotOffers(vars.applicationId),
      });
    },
  });
}
